import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, AbstractControl, FormArray } from '@angular/forms';
import { DynamicFieldConfig, FieldsetConfig } from '../interfaces';
import { ValidationDynamicFormService } from './validation.service';
import { LayoutService } from './layout.service';

@Injectable({
    providedIn: 'root',
})
export class DynamicFormService {
    private fb = inject(FormBuilder);
    private validationService = inject(ValidationDynamicFormService);
    private layoutService = inject(LayoutService);
    /**
     * Cria o formulário baseado na configuração
     */
    createForm(config: DynamicFieldConfig[]): FormGroup {
        const form = this.fb.group({});

        config.forEach(field => {
            if (field.type !== 'button') {
                let initialValue: any;
                const validators = this.validationService.buildValidators(field);
                const isDisabled = field.disabled ?? false;

                // Configurar valor inicial baseado no tipo
                if (field.type === 'select') {
                    initialValue = '';
                } else if (field.type === 'checkbox') {
                    initialValue = []; // Array vazio para checkboxes múltiplos
                } else {
                    initialValue = null;
                }

                form.addControl(
                    field.name,
                    this.fb.control(
                        {
                            value: initialValue,
                            disabled: isDisabled,
                        },
                        validators
                    )
                );
            }
        });

        return form;
    }

    /**
     * Verifica se o formulário é válido
     */
    isFormValid(form: FormGroup): boolean {
        return form.valid && !form.disabled;
    }

    /**
     * Verifica se deve mostrar erro para um campo
     */
    shouldShowError(field: DynamicFieldConfig, control: AbstractControl): boolean {
        return control.invalid && (control.dirty || control.touched);
    }

    /**
     * Verifica se deve mostrar sucesso para um campo
     */
    shouldShowSuccess(field: DynamicFieldConfig, control: AbstractControl): boolean {
        return control.valid && control.dirty && !control.pristine;
    }

    /**
     * Obtém status de validação para classes CSS
     */
    getFieldStatus(field: DynamicFieldConfig, control: AbstractControl): string {
        if (this.shouldShowError(field, control)) {
            return 'has-error';
        }
        if (this.shouldShowSuccess(field, control)) {
            return 'has-success';
        }
        return '';
    }

    /**
     * Obtém mensagem de erro para um campo
     */
    getErrorMessage(field: DynamicFieldConfig, control: AbstractControl): string {
        return this.validationService.getErrorMessage(field, control);
    }

    /**
     * Dispara validação após restaurar dados para mostrar status visual
     */
    triggerValidationAfterRestore(form: FormGroup): void {
        // Aguarda um tick para garantir que o formulário foi atualizado
        setTimeout(() => {
            // Marca todos os campos como touched para mostrar validação
            Object.keys(form.controls).forEach(key => {
                const control = form.get(key);
                if (control) {
                    // Marca como touched para mostrar status visual
                    control.markAsTouched();

                    // Dispara validação
                    control.updateValueAndValidity();
                }
            });
        }, 0);
    }

    /**
     * Delegates para LayoutService
     */
    getFieldId(field: DynamicFieldConfig): string {
        return this.layoutService.getFieldId(field);
    }

    getFieldGroup(field: DynamicFieldConfig): string {
        return this.layoutService.getFieldGroup(field);
    }

    getFieldsetByGroup(group: string, fieldsets: FieldsetConfig[]): FieldsetConfig | undefined {
        return this.layoutService.getFieldsetByGroup(group, fieldsets);
    }

    getFieldsByGroup(group: string, config: DynamicFieldConfig[]): DynamicFieldConfig[] {
        return this.layoutService.getFieldsByGroup(group, config);
    }

    getUniqueGroups(config: DynamicFieldConfig[]): string[] {
        return this.layoutService.getUniqueGroups(config);
    }

    getFieldGridStyle(field: DynamicFieldConfig): any {
        return this.layoutService.getFieldGridStyle(field);
    }

    isFieldReadonly(field: DynamicFieldConfig): boolean {
        return this.layoutService.isFieldReadonly(field);
    }

    getFieldAutocomplete(field: DynamicFieldConfig): string {
        return this.layoutService.getFieldAutocomplete(field);
    }

    getSubmitButtonClasses(submitButtonColor: string, isFormValid: boolean): any {
        return this.layoutService.getSubmitButtonClasses(submitButtonColor, isFormValid);
    }
}
